import { LitElement, html, css, unsafeCSS, property } from "lit-element";
import { eventCodesApi } from "../../api/eventCodesApi";
import { reportsApi } from "../../api/reportsApi";
import { DropdownModel } from "../../models/components/DropdownModel";
import { ReportConfigModel } from "../../models/report/ReportConfigModel";
import { ReportEventCodeModel } from "../../models/report/ReportEventCodeModel";
import { ReportEventCodeSetModel } from "../../models/report/ReportEventCodeSetModel";
import { ReportEventItemTypeOptionModel } from "../../models/report/ReportEventItemTypeOptionModel";
import { TemplateModel } from "../../models/template/TemplateModel";
import Dropdown from "../dropdown/dropdown";
import ReportConfigView from "../report-config-view/report-config-view";
import ReportTemplateView from "../report-template-view/report-template-view";

import reportBuilderStyles from "./report-builder.scss";

import { v4 as uuid } from "uuid";

import { TeamModel } from "../../models/template/TeamModel";

export default class ReportBuilder extends LitElement {
    @property({ type: Array })
    eventCodeSets: ReportEventCodeSetModel[];

    @property({ type: Array })
    eventCodes: ReportEventCodeModel[];

    @property({ type: Array })
    eventItemTypeOptions: ReportEventItemTypeOptionModel[];

    @property({ type: Object })
    selectedEventCodeSet: ReportEventCodeSetModel;

    @property({ type: Object })
    selectedReportConfig: ReportConfigModel;

    @property({ type: Object })
    selectedReportTemplate: TemplateModel;

    @property({ type: Object })
    reportConfig: ReportConfigModel;

    @property({ type: Object })
    reportTemplate: TemplateModel;

    @property({ type: Object })
    reportConfigs: ReportConfigModel[];

    @property({ type: Object })
    reportTemplates: TemplateModel[];

    private _eventCodesApi: eventCodesApi;
    private _reportsApi: reportsApi;

    private _baseUrl = "";
    private _userId = "";

    private _reportContainer: HTMLElement;

    private _showTemplate: boolean = false;
    private _showReport: boolean = false;
    private _showLoading: boolean = false;

    constructor(reportContainer: HTMLElement, baseUrl: string, userId: string) {
        super();

        this._baseUrl = baseUrl;
        this._userId = userId;

        this._eventCodesApi = new eventCodesApi(this._baseUrl, this._userId);
        this._reportsApi = new reportsApi(this._baseUrl, this._userId);

        this._reportContainer = reportContainer;

        this.eventCodeSets = [];
        this.eventCodes = [];
        this.eventItemTypeOptions = [];
        this.reportConfigs = [];
        this.reportTemplates = [];
    }

    eventCodeSetChange = async (value: string, eventCodeSetModel: ReportEventCodeSetModel) => {
        this.selectedEventCodeSet = eventCodeSetModel;

        if (eventCodeSetModel) {
            [this.eventCodes, this.eventItemTypeOptions] = await Promise.all([
                this._eventCodesApi.getEventCodes(this.selectedEventCodeSet.eventCodeSetGuid),
                this._eventCodesApi.getEventItemTypeOptions(this.selectedEventCodeSet.eventCodeSetGuid)]);

            for (let [index, eventCode] of Object.entries(this.eventCodes)) {
                if (eventCode.parentEventCodeGuid) {
                    let parentEventCode = this.eventCodes.find((parentEventCode) => parentEventCode.eventCodeGuid == eventCode.parentEventCodeGuid);

                    if (parentEventCode) {
                        eventCode.name += ` (${parentEventCode.name})`;
                    }
                }
            }

            this.eventCodes.sort((a, b) => this.textSort(a, b, "name"));
            this.eventItemTypeOptions.sort((a, b) => this.textSort(a, b, "name"));
        }
    }

    textSort(a, b, property) {
        let returnValue = 0;

        if (a[property] < b[property])
            returnValue = -1;
        else
            returnValue = 1;

        return returnValue;
    }

    addTemplate() {
        this.reportTemplate = <TemplateModel>{
            name: '',
            reportTemplateId: uuid(),
            reportId: this.selectedReportConfig.reportId,
            homeTeam: <TeamModel>{
                color: ''
            },
            awayTeam: <TeamModel>{
                color: ''
            },
            baseColor: '',
            subTemplates: [],
            rows: []
        };

        this._showTemplate = true;
        this._showReport = false;
        this._showLoading = false;
    }

    addReport() {
        this.reportConfig = <ReportConfigModel>{
            reportId: uuid(),
            name: '',
            reportConfigMetrics: [],
            metricCalculations: [],
            secondMetricCalculations: [],
            eventCodeType: 1
        };

        this._showTemplate = false;
        this._showReport = true;
        this._showLoading = false;
    }

    loadReport = async () => {
        this._showLoading = true;

        this.reportConfig = await this._reportsApi.getReportConfig(this._userId, this.selectedReportConfig.reportId);

        this._showTemplate = false;
        this._showReport = true;

        this._showLoading = false;
    }

    loadTemplate = async () => {
        this._showLoading = true;

        this.reportTemplate = await this._reportsApi.getTemplateFromReports(this._userId, this.selectedReportTemplate.reportTemplateId);

        this._showTemplate = true;
        this._showReport = false;

        this._showLoading = false;
    }

    async firstUpdated() {
        [this.eventCodeSets, this.reportConfigs, this.reportTemplates] = await Promise.all([
            this._eventCodesApi.getEventCodeSets(this._userId),
            this._reportsApi.getReportConfigs(this._userId),
            this._reportsApi.getReportTemplates(this._userId)]);
    }

    createEventCodeSet() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select an EventCodeSet", null));
        for (let [i, eventCodeSet] of Object.entries(this.eventCodeSets)) {
            dropdownItems.push(new DropdownModel(eventCodeSet.eventCodeSetGuid, eventCodeSet.name, eventCodeSet));
        }

        let selectedValue = this.selectedEventCodeSet ? this.selectedEventCodeSet.eventCodeSetGuid : undefined;

        return new Dropdown(dropdownItems, this.eventCodeSetChange, selectedValue, "Event Code Set");
    }

    createReportTemplates() {
        let dropdownItems: DropdownModel[] = [];

        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Template", null));
        for (let [i, reportTemplate] of Object.entries(this.reportTemplates)) {
            dropdownItems.push(new DropdownModel(reportTemplate.reportTemplateId, reportTemplate.name, reportTemplate));
        }

        let selectedValue = this.selectedReportTemplate ? this.selectedReportTemplate.reportTemplateId : undefined;

        return new Dropdown(dropdownItems, this.reportTemplateChange, selectedValue, "Report Template");
    }

    createReportConfigs() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Config", null));
        for (let [i, reportConfig] of Object.entries(this.reportConfigs)) {
            dropdownItems.push(new DropdownModel(reportConfig.reportId, reportConfig.name, reportConfig));
        }

        let selectedValue = this.selectedReportConfig ? this.selectedReportConfig.reportId : undefined;

        return new Dropdown(dropdownItems, this.reportConfigChange, selectedValue, "Report Config");
    }

    reportTemplateChange = async (value: string, reportTemplate: TemplateModel) => {
        this.selectedReportTemplate = reportTemplate;
    }

    reportConfigChange = async (value: string, reportConfigModel: ReportConfigModel) => {
        this.selectedReportConfig = reportConfigModel;
    }

    async saveReportConfig() {
        await this._reportsApi.postReportConfig(this._userId, this.reportConfig);
    }

    async saveReportTemplate() {
        await this._reportsApi.postReportTemplate(this._userId, this.reportTemplate);
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return [
            html`   
                <style>
                    ${css`${unsafeCSS(reportBuilderStyles)}`}                     
                </style>
            
                <div class="container">
                        <div class="row">
                            <div class="col d-flex">
                                ${this.eventCodeSets.length > 0 ? this.createEventCodeSet() : ''}
                            </div>                                                        
                        </div>
                        <div class="row">  
                            <div class="col">
                                ${this.reportConfigs.length > 0 ? this.createReportConfigs() : ''}  
                            </div>
                            <div class="col align-self-end">                                                                 
                                <button class="btn btn-info w-100" @click=${this.loadReport}>Load report</button>
                            </div>
                            <div class="col align-self-end">
                                <button class="btn btn-info w-100" @click=${() => this.addReport()}>Add report</button>
                            </div>
                            <div class="col align-self-end">
                                <button class="btn btn-info w-100" @click=${this.saveReportConfig}>Save Report Config</button>
                            </div>
                        </div>

                        <div class="row">    
                            <div class="col align-self-end">
                                ${this.reportTemplates.length > 0 ? this.createReportTemplates() : ''} 
                            </div> 
                            <div class="col align-self-end">
                                <button class="btn btn-info w-100" @click=${this.loadTemplate}>Load template</button>
                            </div>
                            <div class="col align-self-end">
                                <button class="btn btn-info w-100" @click=${() => this.addTemplate()}>Add template</button>
                            </div>
                            <div class="col align-self-end">
                                <button class="btn btn-info w-100" @click=${this.saveReportTemplate}>Save Report Template</button>
                            </div>
                        </div>

                    <div class="row">
                        <div class="loading-container ${this._showLoading ? "" : "d-none"}">                        
                            <paper-spinner></paper-spinner>
                        </div>
                        <div class="col ${this._showReport ? "" : "d-none"}">
                            ${this._showReport ? new ReportConfigView(this.reportConfig, this.eventCodes, this.eventItemTypeOptions) : ''}                                        
                        </div>
                        <div class="col ${this._showTemplate ? "" : "d-none"}">
                            ${this._showTemplate ? new ReportTemplateView(this.reportTemplate, this.reportConfig, this._userId) : ''}
                        </div>
                    </div>
                </div>`
        ];
    }
}

window.customElements.define('stratus-stats-report-builder', ReportBuilder as any);